import React from 'react';
import {
  ColorPrimitive,
  CustomRawPrimitive,
} from '@packages/shared/LegacyStyleSystem/utils/StylePanelUtils';
import {HSLA, colorToHSL} from '@packages/systems/core/utils/ColorUtils';
import {
  VariableReference,
  useStyleVariableBindMenu,
  VariableMap,
  AnyVariableValue,
  OnChangeMetaType,
} from '@packages/systems/core/utils/StyleVariables';
import {Text} from '@packages/systems/spring';
import {extractCssCustomValue} from '@packages/systems/style/raw-values';
import {hslaToCssString} from './hslaToCssString';
import {BindableColorValue} from '@packages/systems/component-canvas/types';

type FrameBackgroundColorBindingMenuProps = {
  colorValueForBindMenu:
    | ColorPrimitive
    | VariableReference
    | CustomRawPrimitive;
  onBindingChange: (
    value: HSLA | VariableReference | CustomRawPrimitive
  ) => void;
  isBindingMenuOpen: boolean;
  setIsBindingMenuOpen: (isOpen: boolean) => void;
};

/**
 * A wrapper around `StyleVariableBindMenu` that is tailored for binding a Frame's
 * background color to a variable.
 */
export const FrameBackgroundColorBindingMenu = ({
  colorValueForBindMenu,
  onBindingChange,
  isBindingMenuOpen,
  setIsBindingMenuOpen,
}: FrameBackgroundColorBindingMenuProps) => {
  const StyleVariableBindMenu = useStyleVariableBindMenu();

  const chooseColorVariables = (variables: VariableMap) => {
    return variables.filter((variable) => variable.type === 'color');
  };

  const onBindingValueChanged = React.useCallback(
    (value: AnyVariableValue, meta?: OnChangeMetaType) => {
      // NOTE: We use `meta &&` here instead of `meta ?` because when unlinking
      // from a custom value, `meta` is `undefined`.
      if (meta && meta.changeType !== 'changeEnd') return;
      if (
        value.type !== 'color' &&
        value.type !== 'ref' &&
        value.type !== 'raw'
      ) {
        return;
      }

      let finalValue: HSLA | VariableReference | CustomRawPrimitive;
      if (value.type === 'raw') {
        finalValue = {
          type: 'raw',
          value: extractCssCustomValue(value.value),
        };
      } else if (value.type === 'color') {
        finalValue = colorToHSL(value.value);
      } else {
        finalValue = value;
      }

      onBindingChange(finalValue);
    },
    [onBindingChange]
  );

  return (
    <StyleVariableBindMenu
      cssPropertyName="background-color"
      validationStrategy={validationStrategy}
      value={colorValueForBindMenu}
      chooseVariables={chooseColorVariables}
      defaultValue={defaultColorValue}
      onChange={onBindingValueChanged}
      onMenuVisibilityChange={setIsBindingMenuOpen}
      transparentMask
      showStyleVariablesPanelButton={false}
      showEditVariableButton={false}
      showAddButton={false}
      showCustomTab
      shouldUpdateEphemerally={false}
      EmptyState={EmptyState}
      analyticsLocation="component_canvas__frame_settings__background_color"
      isOpen={isBindingMenuOpen}
      data-automation-id="frame-background-color-binding"
    />
  );
};

const EmptyState = () => <Text as="span">No bindable color variables</Text>;

const validationStrategy = {
  type: 'variable',
  variableType: 'color',
} as const;

const defaultColorValue = {type: 'color', value: 'white'} as const;

/**
 * Converts the provided `BindableColorValue` obtained from a Frame's expression
 * to an `AnyVariableValue` that can be used by a Frame's variable binding menu to
 * enable binding the background color to a variable.
 */
export function colorExpressionValueToAnyVariableValue(
  expressionValue: BindableColorValue,
  bodyBackgroundColor: ColorPrimitive | VariableReference | CustomRawPrimitive
): ColorPrimitive | VariableReference | CustomRawPrimitive {
  if (expressionValue.type === 'raw') {
    return expressionValue;
  }

  if (expressionValue.type === 'ref') {
    return expressionValue;
  }

  if (expressionValue.type === 'body-background-color') {
    return bodyBackgroundColor;
  }

  return {
    type: 'color',
    value: hslaToCssString(expressionValue),
  };
}
